import React, { useState, useMemo, memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  oneDark,
  oneLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/hooks/use-theme";
import "@/styles/markdown-styles.css";

interface MarkdownViewerProps {
  content: string;
  darkMode?: boolean;
}

// Gelişmiş kod bloğu algılayıcı fonksiyon
function preprocessMarkdown(md: string): string {
  const lines = md.split(/\r?\n/);
  const codeLangs = [
    "python",
    "javascript",
    "js",
    "java",
    "c",
    "cpp",
    "c++",
    "c#",
    "cs",
    "go",
    "ruby",
    "php",
    "swift",
    "kotlin",
    "typescript",
    "ts",
    "bash",
    "shell",
    "html",
    "css",
    "json",
    "yaml",
    "xml",
    "sql",
    "r",
    "dart",
    "scala",
    "perl",
    "rust",
  ];
  let result: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (codeLangs.includes(line.toLowerCase())) {
      const lang = line;
      let j = i + 1;
      // Eğer açıklama satırı varsa (ör: cpp // ... veya python # ...), onu atla
      if (j < lines.length && /^(\/\/|#|--)/.test(lines[j].trim())) {
        result.push(lines[j]); // açıklamayı kod bloğu dışında bırak
        j++;
      }
      // Kod bloğu başlat
      let codeBlock: string[] = [];
      // İlk boş satıra veya başlığa kadar kodu topla
      while (
        j < lines.length &&
        lines[j].trim() !== "" &&
        !/^#+ /.test(lines[j])
      ) {
        codeBlock.push(lines[j]);
        j++;
      }
      if (codeBlock.length > 0) {
        result.push(`\
\
\`\`\`${lang}\n${codeBlock.join("\n")}\n\`\`\`\n`);
      }
      i = j;
    } else {
      result.push(lines[i]);
      i++;
    }
  }
  return result.join("\n");
}

// Memoized CodeBlock component
const CodeBlock = memo(function CodeBlock({
  children,
  language,
}: {
  children: string;
  language?: string;
}) {
  const [copied, setCopied] = useState(false);
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const handleCopy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative group my-4">
      <div
        className={`flex items-center justify-between px-3 pt-2 pb-1.5 rounded-t-lg border-b ${
          isDark
            ? "bg-black/60 border-zinc-700 text-zinc-300"
            : "bg-zinc-100 border-zinc-300 text-zinc-700"
        }`}
        style={{ position: "relative" }}
      >
        <span className="text-xs font-mono">{language || t("common.code")}</span>
        <button
          onClick={handleCopy}
          className={`copy-btn px-3 py-1 text-xs font-semibold rounded shadow transition-all duration-200 ml-2 border
            ${
              isDark
                ? "bg-zinc-800 text-zinc-100 hover:bg-zinc-700 border-zinc-700"
                : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 border-zinc-300"
            }
          `}
          style={{ minWidth: 70, height: 28, alignSelf: "flex-start" }}
        >
          {copied ? (
            <span className="flex items-center gap-1">
              {t("common.copied")} <span className="text-green-500">✓</span>
            </span>
          ) : (
            t("common.copy")
          )}
        </button>
      </div>
      <div
        className={isDark ? "syntax-highlight-dark" : "syntax-highlight-light"}
      >
        <SyntaxHighlighter
          style={isDark ? oneDark : oneLight}
          language={language}
          customStyle={{
            margin: 0,
            padding: "0.75rem",
            paddingTop: "0.5rem",
            borderRadius: "0 0 0.5rem 0.5rem",
            fontSize: "0.875rem",
            lineHeight: "1.5",
          }}
          PreTag="div"
          codeTagProps={{
            style: {
              fontFamily:
                "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
            },
          }}
        >
          {children}
        </SyntaxHighlighter>
      </div>
    </div>
  );
});

// Memoized MarkdownViewer component
export const MarkdownViewer: React.FC<MarkdownViewerProps> = memo(
  function MarkdownViewer({ content }) {
    // Cache preprocessMarkdown result
    const processed = useMemo(() => preprocessMarkdown(content), [content]);

    // Memoize components config
    const components = useMemo(
      () => ({
        code({ node, inline, className, children, ...props }: any) {
          const match = /language-(\w+)/.exec(className || "");
          if (!inline) {
            return (
              <CodeBlock language={match ? match[1] : undefined}>
                {String(children).replace(/\n$/, "")}
              </CodeBlock>
            );
          }
          return (
            <code
              className="prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono"
              {...props}
            >
              {children}
            </code>
          );
        },
        table({ node, ...props }: any) {
          return (
            <div className="overflow-x-auto my-4 -mx-4 sm:mx-0">
              <table
                className="min-w-full border-collapse border border-border"
                {...props}
              />
            </div>
          );
        },
        thead({ node, ...props }: any) {
          return <thead className="bg-muted" {...props} />;
        },
        th({ node, ...props }: any) {
          return (
            <th
              className="border border-border px-3 py-2 text-left text-sm font-semibold text-foreground"
              {...props}
            />
          );
        },
        td({ node, ...props }: any) {
          return (
            <td
              className="border border-border px-3 py-2 text-sm text-foreground"
              {...props}
            />
          );
        },
      }),
      []
    );

    return (
      <div className="prose dark:prose-invert max-w-none text-md font-normal text-foreground prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-p:font-normal prose-p:my-0 prose-strong:text-foreground prose-strong:font-semibold prose-code:text-foreground prose-pre:bg-transparent prose-pre:p-0 prose-pre:m-0">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {processed}
        </ReactMarkdown>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if content actually changed
    return prevProps.content === nextProps.content;
  }
);
